import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router"
import useTitle from "@/hooks/useTitle"
import EtBtnFormBasic from "@/components/common/buttons/EtBtnFormBasic"
import PulseLoader from "react-spinners/PulseLoader"
import Modal from "@/components/common/modal/Modal"

import {
  useGetUserSchedulingPagesQuery,
  useDeleteUserSchedulingPageMutation,
  useUpdateUserSchedulingPageMutation,
} from "@/features/userSchedulingPages/userSchedulingPagesApiSlice"

const EtDeleteUserSchedulingPage = ({ id, setActiveAdminView }) => {
  
  // First Created - 06 May 2026
  // Author - Atul Pandey
  // Purpose - To delete or deactivate a Trinity SlotBoard scheduling page.
  
  useTitle("Delete SlotBoard Page")

  const { userSchedulingPage } = useGetUserSchedulingPagesQuery(undefined, {
    selectFromResult: ({ data }) => ({
      userSchedulingPage: data?.entities[id],
    }),
  })

  if (!userSchedulingPage) {
    return <PulseLoader color={"#ea580c"} />
  }

  return (
    <EtDeleteUserSchedulingPageForm
      userSchedulingPage={userSchedulingPage}
      id={id}
      setActiveAdminView={setActiveAdminView}
    />
  )
}

const EtDeleteUserSchedulingPageForm = ({
  userSchedulingPage,
  id,
  setActiveAdminView,
}) => {
  const navigate = useNavigate()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [deleteMode, setDeleteMode] = useState("soft")

  const [
    deleteUserSchedulingPage,
    { isLoading: isDeleting, isSuccess: isDeleteSuccess, isError: isDeleteError, error: deleteError },
  ] = useDeleteUserSchedulingPageMutation()

  const [
    updateUserSchedulingPage,
    { isLoading: isUpdating, isSuccess: isUpdateSuccess, isError: isUpdateError, error: updateError },
  ] = useUpdateUserSchedulingPageMutation()

  const [formData, setFormData] = useState({
    pageName: userSchedulingPage.pageName || "",
    publicSlug: userSchedulingPage.publicSlug || "",
    pageDescription: userSchedulingPage.pageDescription || "",
    googleCalendarId: userSchedulingPage.googleCalendarId || "primary",
    isActive: userSchedulingPage.isActive ?? true,
  })

  useEffect(() => {
    if (isDeleteSuccess || isUpdateSuccess) {
      setFormData({
        pageName: "",
        publicSlug: "",
        pageDescription: "",
        googleCalendarId: "",
        isActive: true,
      })

      if (setActiveAdminView) {
        setActiveAdminView("userSchedulingPages")
      } else {
        navigate("/userSchedulingPages")
      }
    }
  }, [isDeleteSuccess, isUpdateSuccess])

  const handleOpenModal = (mode = "soft") => {
    setDeleteMode(mode)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => setIsModalOpen(false)

  const handleConfirm = async e => {
    e.preventDefault()

    try {
      if (deleteMode === "soft") {
        await updateUserSchedulingPage({
          schedulingPageId: id,
          isActive: false,
        }).unwrap()
      } else {
        await deleteUserSchedulingPage({ schedulingPageId: id }).unwrap()
      }

      if (setActiveAdminView) {
        setActiveAdminView("userSchedulingPages")
      } else {
        navigate("/userSchedulingPages")
      }
    } catch (error) {
      console.error(error)
    } finally {
      handleCloseModal()
    }
  }

  const handleCancel = () => {
    if (setActiveAdminView) {
      setActiveAdminView("userSchedulingPages")
    } else {
      navigate("/userSchedulingPages")
    }
  }

  const isLoading = isDeleting || isUpdating
  const isError = isDeleteError || isUpdateError
  const error = deleteError || updateError

  return (
    <div className="bg-gray-200 p-1 w-3/4 mx-auto mt-5 rounded-lg shadow-md shadow-black">
      {isLoading && <PulseLoader color={"#ea580c"} />}

      {isError && (
        <p className="text-orange-700 font-semi-bold py-2">
          {error?.data?.message}
        </p>
      )}

      <div className="bg-white p-1 m-1 rounded">
        <p className="p-1 m-1 font-bold font-poppins text-xl">
          Delete SlotBoard Page
        </p>

        <div className="bg-gray-200 rounded m-1 p-1">
          <form
            onSubmit={e => {
              e.preventDefault()
              handleOpenModal("soft")
            }}
            className="m-1 p-1 text-xs font-poppins"
          >
            <input
              type="text"
              name="pageName"
              placeholder="SlotBoard page name"
              value={formData.pageName}
              disabled
              className="w-full rounded p-2 m-1 bg-gray-100"
            />

            <input
              type="text"
              name="publicSlug"
              placeholder="Public slug"
              value={formData.publicSlug}
              disabled
              className="w-full rounded p-2 m-1 bg-gray-100"
            />

            <textarea
              name="pageDescription"
              placeholder="Page description"
              value={formData.pageDescription}
              disabled
              className="w-full rounded p-2 m-1 bg-gray-100"
              rows={4}
            />

            <input
              type="text"
              name="googleCalendarId"
              placeholder="Google Calendar ID"
              value={formData.googleCalendarId}
              disabled
              className="w-full rounded p-2 m-1 bg-gray-100"
            />

            <div className="bg-orange-50 border border-orange-200 rounded m-1 p-2">
              <p className="text-xs text-orange-700">
                You can either deactivate this SlotBoard page or permanently
                delete it. Deactivation keeps the page and its slots in history.
                Hard delete removes the page if there are no booked slots.
              </p>
            </div>

            <div id="userSchedulingPages" className="bg-white m-1 px-1 py-3 rounded">
              <EtBtnFormBasic
                name={"Make Inactive"}
                type={"clear"}
                handleClick={() => handleOpenModal("soft")}
              />

              <EtBtnFormBasic
                name={"Hard Delete"}
                type={"create"}
                handleClick={() => handleOpenModal("hard")}
              />

              <EtBtnFormBasic
                name={"Cancel"}
                type={"cancel"}
                handleClick={handleCancel}
              />
            </div>
          </form>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirm}
      />
    </div>
  )
}

export default EtDeleteUserSchedulingPage